import React, { useRef, useState } from "react";
import { useBrands } from "../contexts/BrandContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Building2, Upload, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import QuickSearchBar, { useQuickFilter } from "../components/QuickSearchBar";
import ExportCsvButton from "../components/ExportCsvButton";
import StickyPageHeader from "../components/StickyPageHeader";
import NonWorkingDaysTab from "../components/NonWorkingDaysTab";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { CalendarX2 } from "lucide-react";

const COLORS = ["#0055FF", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0EA5E9", "#EC4899", "#18181B"];

const TIMEZONES = [
  "Europe/Paris", "Europe/London", "Europe/Berlin", "Europe/Madrid", "Europe/Lisbon",
  "Europe/Brussels", "Europe/Amsterdam", "Europe/Zurich", "Europe/Rome", "Europe/Vienna",
  "Europe/Athens", "Africa/Casablanca", "Africa/Algiers", "Africa/Tunis",
  "Africa/Abidjan", "Africa/Dakar", "Africa/Nairobi", "Africa/Johannesburg",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Mexico_City", "America/Sao_Paulo", "America/Argentina/Buenos_Aires",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Bangkok", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul",
  "Asia/Singapore", "Asia/Hong_Kong", "Australia/Sydney", "Pacific/Auckland",
  "UTC",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Marques() {
  const { user } = useAuth();
  const { brands, refresh } = useBrands();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    color: "#0055FF",
    timezone: "Europe/Paris",
    logo_url: null,
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [filters, setFilters] = useState({ q: "", timezone: "" });
  const [tab, setTab] = useState("brands");

  const tzOptions = Array.from(new Set((brands || []).map((b) => b.timezone).filter(Boolean))).sort().map((tz) => ({ value: tz, label: tz }));

  const filteredBrands = useQuickFilter(brands, filters, {
    text: (b) => `${b.name || ""} ${b.description || ""}`,
    predicates: {
      timezone: (b, value) => (b.timezone || "Europe/Paris") === value,
    },
  });

  const canManage = user?.role === "admin";

  const save = async () => {
    try {
      if (!form.name.trim()) {
        toast.error("Nom requis");
        return;
      }
      if (editing) {
        await api.put(`/brands/${editing.id}`, form);
        toast.success("Département mis à jour");
      } else {
        await api.post("/brands", form);
        toast.success("Département créé");
      }
      setOpen(false);
      setEditing(null);
      setForm({ name: "", description: "", color: "#0055FF", timezone: "Europe/Paris", logo_url: null });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce département ?")) return;
    await api.delete(`/brands/${id}`);
    toast.success("Supprimée");
    refresh();
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name,
      description: b.description || "",
      color: b.color,
      timezone: b.timezone || "Europe/Paris",
      logo_url: b.logo_url || null,
    });
    setOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", color: "#0055FF", timezone: "Europe/Paris", logo_url: null });
    setOpen(true);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5 Mo)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success("Logo téléversé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="marques-page">
      <Tabs value={tab} onValueChange={setTab} className="w-full">
      <StickyPageHeader testid="marques-sticky-header">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase mb-2">Structure</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900" data-testid="marques-title">
            Départements &amp; structures
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">Pilotez plusieurs activités en parallèle avec leur fuseau horaire</p>
        </div>
        {canManage && tab === "brands" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm" data-testid="new-brand-btn">
                <Plus size={14} className="mr-1" /> Nouveau département
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editing ? "Modifier le département" : "Nouveau département"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="label-uppercase mb-1.5 block">Logo / image</Label>
                  <div className="flex items-center gap-3">
                    <div className="h-16 w-16 border border-zinc-200 rounded-sm flex items-center justify-center bg-zinc-50 overflow-hidden">
                      {form.logo_url ? (
                        <img src={`${BACKEND_URL}${form.logo_url}`} alt="logo" className="h-full w-full object-cover" />
                      ) : (
                        <Building2 size={22} className="text-zinc-300" />
                      )}
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,image/gif"
                        onChange={handleFile}
                        className="hidden"
                        data-testid="brand-logo-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid="brand-logo-upload-btn"
                      >
                        <Upload size={12} className="mr-1" /> {uploading ? "Envoi…" : "Téléverser"}
                      </Button>
                      {form.logo_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-600 rounded-sm"
                          onClick={() => setForm((f) => ({ ...f, logo_url: null }))}
                          data-testid="brand-logo-remove-btn"
                        >
                          <XIcon size={12} /> Retirer
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1 font-mono">PNG / JPG / SVG · max 5 Mo</p>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Nom</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Département Premium" data-testid="brand-name" />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Fuseau horaire</Label>
                    <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                      <SelectTrigger data-testid="brand-timezone-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {TIMEZONES.map((tz) => (
                          <SelectItem key={tz} value={tz} data-testid={`tz-${tz}`}>{tz}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Couleur</Label>
                    <div className="flex gap-1.5 flex-wrap">
                      {COLORS.map((c) => (
                        <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                          className={`h-8 w-8 rounded-sm border-2 ${form.color === c ? "border-zinc-900" : "border-transparent"}`}
                          style={{ backgroundColor: c }} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="brand-save-btn">Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <TabsList className="grid grid-cols-2 w-full sm:w-auto sm:inline-flex" data-testid="marques-tabs">
        <TabsTrigger value="brands" data-testid="tab-brands">
          <Building2 size={14} className="mr-1.5" /> Départements
        </TabsTrigger>
        <TabsTrigger value="non-working" data-testid="tab-non-working">
          <CalendarX2 size={14} className="mr-1.5" /> Jours non travaillés
        </TabsTrigger>
      </TabsList>

      {tab === "brands" && (
      <QuickSearchBar
        value={filters}
        onChange={setFilters}
        placeholder="Rechercher un département (nom, description)…"
        testid="brands-search"
        filters={[
          { key: "timezone", label: "Fuseau horaire", type: "select", placeholder: "Tous les fuseaux", options: tzOptions },
        ]}
      />
      )}

      {tab === "brands" && (
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-zinc-500 font-mono" data-testid="brands-result-count">
          {filteredBrands.length} résultat{filteredBrands.length > 1 ? "s" : ""} sur {(brands || []).length}
        </p>
        <ExportCsvButton
          filename="departements"
          testid="brands-export-csv"
          rows={filteredBrands}
          columns={[
            { label: "Nom", value: (b) => b.name },
            { label: "Description", value: (b) => b.description || "" },
            { label: "Fuseau horaire", value: (b) => b.timezone || "Europe/Paris" },
            { label: "Couleur", value: (b) => b.color || "" },
          ]}
        />
      </div>
      )}
      </StickyPageHeader>

      <TabsContent value="brands" className="mt-0 focus-visible:outline-none">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredBrands.map((b) => (
          <div key={b.id} className="bg-white border border-zinc-200 rounded-sm p-6 hover:border-zinc-300 transition-colors" data-testid={`brand-card-${b.id}`}>
            <div className="flex items-start justify-between mb-4">
              {b.logo_url ? (
                <img
                  src={`${BACKEND_URL}${b.logo_url}`}
                  alt={b.name}
                  className="h-12 w-12 object-cover rounded-sm border border-zinc-200"
                />
              ) : (
                <div
                  className="h-12 w-12 rounded-sm flex items-center justify-center"
                  style={{ backgroundColor: b.color + "20", color: b.color }}
                >
                  <Building2 size={22} strokeWidth={1.5} />
                </div>
              )}
              {canManage && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-brand-${b.id}`}>
                    <Pencil size={12} />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(b.id)} data-testid={`delete-brand-${b.id}`}>
                    <Trash2 size={12} />
                  </Button>
                </div>
              )}
            </div>
            <h3 className="font-display text-xl font-bold text-zinc-900 tracking-tight">{b.name}</h3>
            <p className="text-sm text-zinc-600 mt-2 line-clamp-2">{b.description || "—"}</p>
            <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                {b.timezone || "Europe/Paris"}
              </p>
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
            </div>
          </div>
        ))}
        {filteredBrands.length === 0 && (
          <div className="col-span-3 text-center py-12 text-sm text-zinc-500 font-mono bg-white border border-zinc-200 rounded-sm">
            {brands.length === 0 ? "Aucun département. Créez votre première activité." : "Aucun département ne correspond à votre recherche."}
          </div>
        )}
      </div>
      </TabsContent>

      <TabsContent value="non-working" className="mt-0 focus-visible:outline-none">
        <NonWorkingDaysTab brands={brands || []} canManage={canManage} />
      </TabsContent>
      </Tabs>
    </div>
  );
}
